import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";

type Props = {
    /** The preset the menu was opened on, or null for the "All [Screen]" tab */
    preset: IFilterPresetRuntime | null;
    /** True when no default preset applies (the "All" tab is the initial view) */
    isAllDefault: boolean;
    x: number;
    y: number;
    translate: (id: string) => string;
    onDelete: (presetId: string) => void;
    onToggleDefault: (presetId: string) => void;
    onSetAllAsDefault: () => void;
    /** Opens the manage sidebar (reorder / rename / delete). Hidden when there are no presets. */
    onManage?: () => void;
    onClose: () => void;
};

/**
 * Right-click menu for a preset tab: set/clear the default view, delete the preset.
 * On the "All [Screen]" tab it offers making the baseline the default view.
 * Closes on outside click, Escape, scroll or resize.
 */
export const PresetContextMenu = (props: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const { onClose, preset, translate } = props;

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("scroll", onClose, true);
        window.addEventListener("resize", onClose);

        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("scroll", onClose, true);
            window.removeEventListener("resize", onClose);
        };
    }, [onClose]);

    return createPortal(
        <div ref={ref} className={"FilterBar-context-menu"} style={{ top: props.y, left: props.x }} role={"menu"}>
            {preset ? (
                <>
                    <button
                        type={"button"}
                        className={"FilterBar-context-item"}
                        role={"menuitem"}
                        onClick={() => props.onToggleDefault(preset.id)}>
                        <i className={(preset.isDefault ? "far fa-star" : "fas fa-star") + " me-2"} />
                        {translate(preset.isDefault ? "clear_default" : "set_as_default")}
                    </button>
                    <button
                        type={"button"}
                        className={"FilterBar-context-item text-danger"}
                        role={"menuitem"}
                        onClick={() => props.onDelete(preset.id)}>
                        <i className={"fas fa-trash-alt me-2"} />
                        {translate("delete_preset")}
                    </button>
                </>
            ) : (
                <button
                    type={"button"}
                    className={"FilterBar-context-item"}
                    role={"menuitem"}
                    disabled={props.isAllDefault}
                    onClick={props.onSetAllAsDefault}>
                    <i className={(props.isAllDefault ? "fas fa-check" : "fas fa-star") + " me-2"} />
                    {translate(props.isAllDefault ? "is_default_view" : "set_as_default")}
                </button>
            )}
            {props.onManage ? (
                <button type={"button"} className={"FilterBar-context-item"} role={"menuitem"} onClick={props.onManage}>
                    <i className={"fas fa-cog me-2"} />
                    {translate("manage_presets")}
                </button>
            ) : null}
        </div>,
        document.body
    );
};
